import { useCallback, useEffect, useState } from "react";
import { api, errMsg, itDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UploadSimple, Eye, Trash, Folder, FolderPlus, Printer, PencilSimple, BellRinging, Warning, CalendarDots, CaretRight, MagnifyingGlass } from "@phosphor-icons/react";

/** Validità attestati: HACCP 3 anni, Formazione Lavoratori 5 anni dalla data di rilascio. */
const piuAnni = (iso, anni) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || "")) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return `${y + anni}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

const CELL = { scaduta: "bg-rose-500/20 text-rose-700 dark:text-rose-300",
  in_scadenza: "bg-amber-400/25 text-amber-800 dark:text-amber-300",
  valida: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300",
  assente: "bg-transparent text-muted-foreground" };

export default function SafetyDocs() {
  const { user } = useAuth();
  const canWrite = ["admin", "manager", "rspp"].includes(user?.role);
  const [meta, setMeta] = useState(null);
  const [folder, setFolder] = useState("Attestati HACCP");
  const [docs, setDocs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [soloScadenze, setSoloScadenze] = useState(false);
  const [cerca, setCerca] = useState("");
  const [expiring, setExpiring] = useState(null);
  const [form, setForm] = useState(null);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const info = (meta?.folders || []).find((f) => f.name === folder) || {};
  const mode = info.mode || "upload";
  // anni di validità dell'attestato della cartella corrente
  const anniValidita = folder === "Formazione Lavoratori" ? 5 : 3;

  const [subs, setSubs] = useState(null);
  const [sub, setSub] = useState("");
  const [newSub, setNewSub] = useState(null);
  const [scad, setScad] = useState(null);

  const salvaScadenza = async () => {
    try {
      await api.put(`/safety-docs/documento/${scad.id}/scadenza`, {
        expires_at: scad.expires_at, training_type: scad.training_type,
        issued_at: scad.issued_at || "" });
      toast.success("Scadenza salvata · scadenziario aggiornato");
      setScad(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = useCallback(async () => {
    try {
      const [m, d] = await Promise.all([
        api.get("/safety-docs/folders"),
        api.get("/safety-docs", { params: { folder, subfolder: sub } })]);
      setMeta(m.data); setDocs(d.data);
      const fmeta = (m.data.folders || []).find((f) => f.name === folder);
      if (fmeta?.mode === "upload") setSubs((await api.get("/safety-docs/subfolders", { params: { folder, parent: sub } })).data);
      else setSubs(null);
      if (folder === "Scadenziario") {
        setMatrix((await api.get("/training-matrix")).data);
        setExpiring((await api.get("/training-matrix/expiring")).data);
      }
    } catch (e) { toast.error(errMsg(e)); }
  }, [folder, sub]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/employees", { params: { status: "active" } }).then((r) => setEmployees(r.data)).catch(() => {});
  }, []);

  const apri = async (d) => {
    try {
      const res = await api.get(d.file_url, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const carica = async () => {
    if (!form.files?.length) { toast.error("Scegli i file da caricare"); return; }
    setBusy(true);
    const fd = new FormData();
    Array.from(form.files).forEach((f) => fd.append("files", f));
    try {
      if (mode === "attestati") {
        if (!form.employee_id || !form.training_type) {
          toast.error("Scegli il dipendente e il tipo di attestato"); setBusy(false); return;
        }
        fd.append("employee_id", form.employee_id);
        fd.append("training_type", form.training_type);
        fd.append("expires_at", form.expires_at || "");
        fd.append("issued_at", form.issued_at || "");
        const { data } = await api.post("/safety-docs/attestati", fd);
        toast.success(`${data.caricati} attestati caricati · scadenziario aggiornato`);
      } else {
        fd.append("folder", folder);
        fd.append("subfolder", sub);
        fd.append("notes", form.notes || "");
        const { data } = await api.post("/safety-docs", fd);
        toast.success(`${data.caricati} documenti caricati`);
      }
      setForm(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const elimina = async (d) => {
    if (!window.confirm(`Eliminare "${d.name}"?`)) return;
    const url = d.source === "document" ? `/safety-docs/documento/${d.id}` : `/safety-docs/${d.id}`;
    try { await api.delete(url); toast.success("Documento eliminato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const creaSub = async () => {
    try {
      const { data } = await api.post("/safety-docs/subfolders", { folder, name: newSub, parent: sub });
      toast.success(`Cartella "${data.name}" creata`);
      setNewSub(null); setSub(data.path);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const spostaFile = async (d, dest) => {
    if ((d.subfolder || "") === dest) return;
    try {
      await api.put(`/safety-docs/${d.id}/subfolder`, { subfolder: dest });
      toast.success(`Spostato in "${dest || "Cartella principale"}"`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const rinominaSub = async (percorso, nome) => {
    const nuovo = window.prompt("Nuovo nome della cartella", nome);
    if (!nuovo || nuovo.trim() === nome) return;
    try {
      const { data } = await api.put("/safety-docs/subfolders", { folder, path: percorso, new_name: nuovo.trim() });
      toast.success("Cartella rinominata");
      if (sub === percorso) setSub(data.path); else load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const eliminaSub = async (nome) => {
    if (!window.confirm(`Eliminare la cartella "${nome}" con le sue sottocartelle e i documenti?`)) return;
    try {
      const { data } = await api.delete("/safety-docs/subfolders", { params: { folder, name: nome } });
      toast.success(`Cartella eliminata${data.documenti_eliminati ? ` con ${data.documenti_eliminati} documenti` : ""}`);
      if (sub === nome) setSub(""); else load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const salvaDate = async () => {
    try {
      await api.put("/training-matrix", { employee_id: edit.employee_id, type: edit.type,
        expires_at: edit.expires_at || "" });
      toast.success("Scadenziario aggiornato");
      setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const promemoria = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/training-matrix/reminder");
      toast.success(data.expiring
        ? `${data.expiring} attestati in scadenza · ${data.notified.length} email inviate`
        : data.message);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const pdf = async () => {
    try {
      const res = await api.get("/training-matrix/pdf", { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };

  // ricerca libera per nome/cognome nello scadenziario
  const q = cerca.trim().toLowerCase();
  const righeScadenze = (expiring?.rows || []).filter(
    (r) => !q || (r.employee_name || "").toLowerCase().includes(q));
  const righeMatrice = (matrix?.rows || []).filter(
    (r) => !q || `${r.last_name} ${r.first_name} ${r.first_name} ${r.last_name}`.toLowerCase().includes(q));
  const righeDocs = docs.filter(
    (d) => !q || `${d.employee_name || ""} ${d.name || ""}`.toLowerCase().includes(q));

  return (
    <div className="space-y-3" data-testid="safety-docs-page">      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Documenti Sicurezza / RSPP</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {info.note || "Archivio condiviso con l'RSPP: carica più file insieme in ogni cartella documentale."}
          </p>
        </div>
        {canWrite && (mode === "upload" || mode === "attestati") && (
          <Button size="sm" className="rounded-sm font-semibold" data-testid="safety-doc-add"
            onClick={() => setForm({ training_type: info.types?.length === 1 ? info.types[0] : "" })}>
            <UploadSimple size={15} className="mr-1.5" />
            {mode === "attestati" ? "Carica attestati" : "Carica documenti"}
          </Button>
        )}
        {mode === "matrix" && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={soloScadenze ? "default" : "outline"} className="rounded-sm font-semibold"
              data-testid="matrix-only-expiring" onClick={() => setSoloScadenze(!soloScadenze)}>
              <Warning size={15} className="mr-1.5" />
              {soloScadenze ? "Mostra tutto lo scadenziario" : `Solo scaduti e in scadenza${expiring ? ` (${expiring.count})` : ""}`}
            </Button>
            {canWrite && (
              <Button size="sm" variant="outline" className="rounded-sm font-semibold"
                data-testid="matrix-reminder" disabled={busy} onClick={promemoria}>
                <BellRinging size={15} className="mr-1.5" /> Invia promemoria scadenze
              </Button>
            )}
            <Button size="sm" variant="outline" className="rounded-sm font-semibold" data-testid="matrix-pdf" onClick={pdf}>
              <Printer size={15} className="mr-1.5" /> Esporta PDF
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="safety-folders">
        {(meta?.folders || []).map((f) => (
          <button key={f.name} type="button" data-testid={`safety-folder-${f.name}`}
            onClick={() => { setFolder(f.name); setSub(""); setCerca(""); }}
            className={`rounded-sm border px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${
              folder === f.name ? "border-primary bg-secondary" : "border-border hover:bg-secondary/50"}`}>
            <Folder size={13} className="inline mr-1.5 -mt-0.5" />{f.name}
            <span className="ml-1.5 text-muted-foreground tabular-nums">{f.count}</span>
          </button>
        ))}
      </div>

      {subs && (
        <div className="space-y-1.5" data-testid="safety-subfolders">
          <div className="flex flex-wrap items-center gap-1 text-xs" data-testid="safety-breadcrumb">
            <button type="button" data-testid="safety-sub-root" onClick={() => setSub("")}
              className={`rounded-sm px-2 py-1 font-semibold ${sub === "" ? "bg-secondary" : "hover:bg-secondary/60"}`}>
              {folder}
            </button>
            {sub.split("/").filter(Boolean).map((parte, i, arr) => (
              <span key={parte + i} className="flex items-center gap-1">
                <CaretRight size={11} className="text-muted-foreground" />
                <button type="button" data-testid={`safety-crumb-${parte}`}
                  onClick={() => setSub(arr.slice(0, i + 1).join("/"))}
                  className={`rounded-sm px-2 py-1 font-semibold ${i === arr.length - 1 ? "bg-secondary" : "hover:bg-secondary/60"}`}>
                  {parte}
                </button>
              </span>
            ))}
            <span className="ml-1 text-muted-foreground tabular-nums">{subs.root_count} file</span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {subs.subfolders.map((s2) => (
              <span key={s2.path} className="inline-flex items-center rounded-sm border border-border">
                <button type="button" data-testid={`safety-sub-${s2.name}`} onClick={() => setSub(s2.path)}
                  className="px-2.5 py-1 text-xs font-semibold hover:bg-secondary/50">
                  <Folder size={13} className="inline mr-1.5 -mt-0.5" />{s2.name}
                  <span className="ml-1.5 text-muted-foreground tabular-nums">
                    {s2.count}{s2.children ? ` · ${s2.children} cart.` : ""}
                  </span>
                </button>
                {canWrite && (
                  <>
                    <button type="button" title="Rinomina cartella" data-testid={`safety-sub-rename-${s2.name}`}
                      className="px-1.5 py-1 text-muted-foreground hover:text-foreground"
                      onClick={() => rinominaSub(s2.path, s2.name)}>
                      <PencilSimple size={13} />
                    </button>
                    <button type="button" title="Elimina cartella" data-testid={`safety-sub-del-${s2.name}`}
                      className="px-1.5 py-1 text-rose-600 hover:text-rose-700" onClick={() => eliminaSub(s2.path)}>
                      <Trash size={13} />
                    </button>
                  </>
                )}
              </span>
            ))}
            {canWrite && (
              <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs font-semibold"
                data-testid="safety-sub-add" onClick={() => setNewSub("")}>
                <FolderPlus size={14} className="mr-1.5" /> Nuova cartella
              </Button>
            )}
          </div>
        </div>
      )}

      {mode === "matrix" && (
        <div className="relative max-w-sm">
          <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={cerca} onChange={(e) => setCerca(e.target.value)}
            placeholder="Cerca dipendente per nome o cognome"
            className="h-9 rounded-sm pl-8" data-testid="matrix-search" />
        </div>
      )}

      {mode === "matrix" && soloScadenze && (
        <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin" data-testid="expiring-panel">
          <table className="w-full text-sm" data-testid="expiring-table">
            <thead className="bg-secondary"><tr>
              {["DIPENDENTE", "CODICE", "POSTAZIONE", "ATTESTATO", "SCADENZA", "STATO"].map((h) => (
                <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(righeScadenze || []).map((r) => (
                <tr key={`${r.employee_id}-${r.type}`} className="border-t border-border"
                  data-testid={`expiring-row-${r.code}-${r.type.replace(/\s+/g, "_")}`}>
                  <td className="px-3 py-1.5 font-semibold whitespace-nowrap">{r.employee_name}</td>
                  <td className="px-3 py-1.5 tabular-nums text-muted-foreground">{r.code}</td>
                  <td className="px-3 py-1.5 text-muted-foreground">{r.position || "—"}</td>
                  <td className="px-3 py-1.5">{r.type}</td>
                  <td className="px-3 py-1.5 tabular-nums">{itDate(r.expires_at)}</td>
                  <td className="px-3 py-1.5">
                    <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-bold ${CELL[r.state]}`}>
                      {r.state === "scaduta" ? `SCADUTO DA ${Math.abs(r.days_left)} GG`
                        : `SCADE TRA ${r.days_left} GG`}
                    </span>
                    {canWrite && (
                      <button type="button" className="ml-2 text-xs font-semibold text-primary underline"
                        data-testid={`expiring-edit-${r.code}-${r.type.replace(/\s+/g, "_")}`}
                        onClick={() => setEdit({ employee_id: r.employee_id, type: r.type,
                          name: r.employee_name, expires_at: r.expires_at || "" })}>
                        aggiorna
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {expiring && righeScadenze.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground" data-testid="expiring-empty">
                  {cerca.trim()
                    ? `Nessun attestato in scadenza per "${cerca}"`
                    : `Nessun attestato scaduto o in scadenza nei prossimi ${expiring.days} giorni`}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {mode === "matrix" && matrix && !soloScadenze && (
        <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
          <table className="w-full text-xs bc-no-cards" data-testid="matrix-table">
            <thead className="bg-secondary"><tr>
              {["N.", "COGNOME", "NOME", "POSTAZIONE", ...matrix.types].map((h) => (
                <th key={h} className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {righeMatrice.map((r) => (
                <tr key={r.employee_id} className="border-t border-border" data-testid={`matrix-row-${r.code}`}>
                  <td className="px-2 py-1 tabular-nums">{r.n}</td>
                  <td className="px-2 py-1 font-semibold whitespace-nowrap">{r.last_name}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{r.first_name}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{r.position || "—"}</td>
                  {matrix.types.map((t) => {
                    const c = r.cells[t];
                    return (
                      <td key={t} className="px-1 py-1">
                        <button type="button" disabled={!canWrite}
                          data-testid={`matrix-cell-${r.code}-${t.replace(/\s+/g, "_")}`}
                          onClick={() => setEdit({ employee_id: r.employee_id, type: t,
                            name: `${r.last_name} ${r.first_name}`, expires_at: c.expires_at || "" })}
                          className={`w-full rounded-sm px-1.5 py-1 text-[11px] font-semibold tabular-nums ${CELL[c.state]} ${canWrite ? "hover:opacity-80" : ""}`}>
                          {itDate(c.expires_at) || "\u00A0"}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {mode !== "matrix" && (
        <div className="relative max-w-sm">
          <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={cerca} onChange={(e) => setCerca(e.target.value)}
            placeholder="Cerca dipendente per nome o cognome"
            className="h-9 rounded-sm pl-8" data-testid="safety-docs-search" />
        </div>
      )}

      {mode !== "matrix" && (
        <div className="rounded-sm border border-border bg-card divide-y divide-border">
          {righeDocs.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center gap-2 px-3 py-2" data-testid={`safety-doc-${d.id}`}>
              <div className="min-w-[240px] flex-1">
                <div className="text-sm font-semibold truncate">{d.name}</div>
                <div className="text-[11px] text-muted-foreground">
                  {d.employee_name ? `${d.employee_name} · ` : ""}
                  {d.training_type ? `${d.training_type} · ` : ""}
                  {d.expires_at ? `scade il ${itDate(d.expires_at)} · ` : ""}
                  {d.date ? `${itDate(d.date)} · ` : ""}
                  {d.uploaded_by ? `da ${d.uploaded_by}` : ""}
                </div>
              </div>
              <Button size="sm" variant="outline" className="h-8 rounded-sm" data-testid={`safety-doc-view-${d.id}`}
                onClick={() => apri(d)}>
                <Eye size={14} className="mr-1.5" /> Apri
              </Button>
              {canWrite && mode === "attestati" && (
                <Button size="sm" variant="outline" className="h-8 rounded-sm"
                  data-testid={`safety-doc-expiry-${d.id}`}
                  onClick={() => setScad({ id: d.id, name: d.name, employee_name: d.employee_name,
                    training_type: d.training_type || (info.types?.length === 1 ? info.types[0] : ""),
                    issued_at: d.issued_at || "", expires_at: d.expires_at || "" })}>
                  <CalendarDots size={14} className="mr-1.5" />
                  {d.expires_at ? "Modifica scadenza" : "Aggiungi scadenza"}
                </Button>
              )}
              {canWrite && d.source === "safety_doc" && subs && (
                <Select value={d.subfolder || "__root__"}
                  onValueChange={(v) => spostaFile(d, v === "__root__" ? "" : v)}>
                  <SelectTrigger className="h-8 w-[170px] rounded-sm text-xs" data-testid={`safety-doc-move-${d.id}`}>
                    <SelectValue placeholder="Sposta in..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__root__">Cartella principale</SelectItem>
                    {(subs.all_paths || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {canWrite && d.can_delete && (
                <button type="button" className="text-rose-600 hover:text-rose-700 p-1.5" title="Elimina"
                  data-testid={`safety-doc-del-${d.id}`} onClick={() => elimina(d)}>
                  <Trash size={16} />
                </button>
              )}
            </div>
          ))}
          {righeDocs.length === 0 && (
            <div className="px-3 py-8 text-center text-muted-foreground" data-testid="safety-docs-empty">
              {cerca.trim() ? `Nessun documento trovato per "${cerca}"` : "Nessun documento in questa cartella"}
            </div>
          )}
        </div>
      )}

      {scad && (
        <Dialog open onOpenChange={() => setScad(null)}>
          <DialogContent className="rounded-sm sm:max-w-sm" data-testid="attestato-expiry-dialog">
            <DialogHeader>
              <DialogTitle className="font-head text-base">Scadenza attestato</DialogTitle>
              <DialogDescription className="text-xs">
                {scad.employee_name ? `${scad.employee_name} · ` : ""}{scad.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              {(info.types || []).length > 1 && (
                <Select value={scad.training_type || ""} onValueChange={(v) => setScad({ ...scad, training_type: v })}>
                  <SelectTrigger className="h-9 rounded-sm" data-testid="attestato-expiry-type">
                    <SelectValue placeholder="Tipo di attestato" />
                  </SelectTrigger>
                  <SelectContent>
                    {info.types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <div>
                <Label className="text-[10px] font-semibold uppercase text-muted-foreground">Data di rilascio</Label>
                <Input type="date" className="h-9 rounded-sm mt-1" data-testid="attestato-issued-date"
                  value={scad.issued_at || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setScad({ ...scad, issued_at: v, expires_at: piuAnni(v, anniValidita) || scad.expires_at });
                  }} />
                <p className="text-[10px] text-muted-foreground mt-1">
                  La scadenza viene calcolata automaticamente a {anniValidita} anni dal rilascio e resta modificabile.
                </p>
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase text-muted-foreground">Data di scadenza</Label>
                <Input type="date" className="h-9 rounded-sm mt-1" data-testid="attestato-expiry-date"
                  value={scad.expires_at} onChange={(e) => setScad({ ...scad, expires_at: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setScad(null)}>Annulla</Button>
                <Button size="sm" className="rounded-sm font-semibold" data-testid="attestato-expiry-save"
                  disabled={!scad.expires_at || !scad.training_type} onClick={salvaScadenza}>Salva</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {newSub !== null && (
        <Dialog open onOpenChange={() => setNewSub(null)}>
          <DialogContent className="rounded-sm sm:max-w-sm" data-testid="safety-sub-dialog">
            <DialogHeader>
              <DialogTitle className="font-head">Nuova cartella</DialogTitle>
              <DialogDescription className="text-xs">dentro "{sub || folder}"</DialogDescription>
            </DialogHeader>
            <Input value={newSub} onChange={(e) => setNewSub(e.target.value)} className="h-9 rounded-sm"
              placeholder="Nome della cartella" data-testid="safety-sub-name"
              onKeyDown={(e) => e.key === "Enter" && newSub.trim().length > 1 && creaSub()} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setNewSub(null)}>Annulla</Button>
              <Button size="sm" className="rounded-sm font-semibold" disabled={newSub.trim().length < 2}
                data-testid="safety-sub-save" onClick={creaSub}>Crea</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {form && (
        <Dialog open onOpenChange={() => setForm(null)}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="safety-doc-dialog">
            <DialogHeader>
              <DialogTitle className="font-head">Carica in "{sub || folder}"</DialogTitle>
              <DialogDescription className="text-xs">
                {mode === "attestati"
                  ? "L'attestato viene salvato nei documenti del dipendente e lo scadenziario si aggiorna da solo."
                  : "Puoi selezionare più file contemporaneamente."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Input type="file" multiple data-testid="safety-doc-file" className="h-9 rounded-sm"
                onChange={(e) => setForm({ ...form, files: e.target.files })} />
              {mode === "attestati" && (
                <>
                  <Select value={form.employee_id || ""} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                    <SelectTrigger className="h-9 rounded-sm" data-testid="safety-doc-employee">
                      <SelectValue placeholder="Dipendente" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {employees.map((e) => (
                        <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name} ({e.code})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={form.training_type || ""} onValueChange={(v) => setForm({ ...form, training_type: v })}>
                    <SelectTrigger className="h-9 rounded-sm" data-testid="safety-doc-type">
                      <SelectValue placeholder="Tipo di attestato" />
                    </SelectTrigger>
                    <SelectContent>
                      {(info.types || meta?.training_types || []).map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div>
                    <Label className="text-[10px] font-semibold uppercase text-muted-foreground">Data di rilascio</Label>
                    <Input type="date" className="h-9 rounded-sm mt-1" data-testid="safety-doc-issued"
                      value={form.issued_at || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setForm({ ...form, issued_at: v, expires_at: piuAnni(v, anniValidita) || form.expires_at });
                      }} />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      La scadenza si calcola a {anniValidita} anni dal rilascio e resta modificabile.
                    </p>
                  </div>
                  <div>
                    <Label className="text-[10px] font-semibold uppercase text-muted-foreground">Scadenza</Label>
                    <Input type="date" className="h-9 rounded-sm mt-1" data-testid="safety-doc-expiry"
                      value={form.expires_at || ""} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
                  </div>
                </>
              )}
              {mode === "upload" && (
                <Input placeholder="Note (facoltative)" className="h-9 rounded-sm" data-testid="safety-doc-notes"
                  value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setForm(null)}>Annulla</Button>
                <Button size="sm" className="rounded-sm font-semibold" disabled={busy}
                  data-testid="safety-doc-save" onClick={carica}>
                  {busy ? "Caricamento..." : "Carica"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {edit && (
        <Dialog open onOpenChange={() => setEdit(null)}>
          <DialogContent className="rounded-sm sm:max-w-sm" data-testid="matrix-dialog">
            <DialogHeader>
              <DialogTitle className="font-head text-base">{edit.type}</DialogTitle>
              <DialogDescription className="text-xs">{edit.name}</DialogDescription>
            </DialogHeader>
            <div>
              <Label className="text-[10px] font-semibold uppercase text-muted-foreground">Data di scadenza</Label>
              <Input type="date" className="h-9 rounded-sm mt-1" data-testid="matrix-expiry"
                value={edit.expires_at} onChange={(e) => setEdit({ ...edit, expires_at: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setEdit(null)}>Annulla</Button>
              <Button size="sm" className="rounded-sm font-semibold" data-testid="matrix-save" onClick={salvaDate}>
                <PencilSimple size={14} className="mr-1.5" /> Salva
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
